import React, { useState } from 'react';
import { Eye, Code, Copy, Check, Sparkles, Send, Download } from 'lucide-react';
import { WorkspaceUser, SignatureEmployeeData, SavedSignatureTemplate, UserGroup } from '../types';
import {
  generateClientSignatureHtml,
  DEFAULT_CEREBRO_LOGO,
  downloadSignatureHtmlFile,
  copySignatureToClipboard,
} from '../services/signatureTemplateClient';

interface SignaturePreviewProps {
  activeUser: WorkspaceUser | null;
  savedTemplates?: SavedSignatureTemplate[];
  groups?: UserGroup[];
  activeTemplateConfig?: SavedSignatureTemplate;
  onInjectSingle: (user: WorkspaceUser, signatureHtml: string) => void;
  isInjecting: boolean;
}

export const SignaturePreview: React.FC<SignaturePreviewProps> = ({
  activeUser,
  savedTemplates = [],
  groups = [],
  activeTemplateConfig,
  onInjectSingle,
  isInjecting,
}) => {
  const [activeTab, setActiveTab] = useState<'visual' | 'code'>('visual');
  const [copied, setCopied] = useState(false);

  // Editable overrides for testing live modifications
  const [customTitle, setCustomTitle] = useState('');
  const [customPhone, setCustomPhone] = useState('');

  // Determine assigned template for the active user:
  // 1. Explicit employee override
  // 2. Assigned department / group template (e.g. Zarząd i Dyrekcja -> Modern Horizontal)
  // 3. Global active template or default
  const userGrp = groups.find((g) => g.id === (activeUser?.groupId || 'group_main')) || groups[0];
  const assignedTplId = activeUser?.assignedSignatureId || activeUser?.customTemplateId || userGrp?.savedTemplateId || 'tpl_default';
  const assignedTemplate =
    savedTemplates.find((t) => t.id === assignedTplId || t.templateId === assignedTplId) ||
    activeTemplateConfig ||
    savedTemplates[0];

  // Merge template styling with employee's individual personal info & overrides
  const employeeData: SignatureEmployeeData = {
    fullName: activeUser?.fullName || 'Jan Kowalski',
    jobTitle: customTitle || activeUser?.jobTitle || 'Senior Cloud Architect',
    department: activeUser?.department || 'Cloud Engineering',
    phone: customPhone || activeUser?.phone,
    mobile: activeUser?.mobile,
    showMobile: activeUser?.showMobile,
    useCustomPhone: activeUser?.useCustomPhone,
    email: activeUser?.primaryEmail || 'jan.kowalski@cerebrolabs.io',
    emailAlias: activeUser?.emailAlias,
    avatarUrl: (activeUser?.avatarUrl && !activeUser.avatarUrl.startsWith('data:image/svg')) ? activeUser.avatarUrl : undefined,
    defaultAvatarPlaceholder: assignedTemplate?.defaultAvatarPlaceholder,
    companyName: assignedTemplate?.companyName || 'CerebroLabs Sp. z o.o.',
    companyPhone: assignedTemplate?.companyPhone,
    websiteUrl: assignedTemplate?.websiteUrl || 'https://cerebrolabs.io',
    useCustomWebsite: activeUser?.useCustomWebsite,
    customWebsiteUrl: activeUser?.customWebsiteUrl,
    useCustomAddress: activeUser?.useCustomAddress,
    customAddress: activeUser?.customAddress,
    nip: assignedTemplate?.nip || '5252824941',
    regon: assignedTemplate?.regon || '386928102',
    address: assignedTemplate?.address || 'ul. Prosta 20, 00-850 Warszawa',
    logoUrl: (assignedTemplate?.logoUrl && !assignedTemplate.logoUrl.startsWith('data:image/svg'))
      ? assignedTemplate.logoUrl
      : DEFAULT_CEREBRO_LOGO,
    logoWidth: assignedTemplate?.logoWidth,
    logoHeight: assignedTemplate?.logoHeight,
    templateId: assignedTemplate?.templateId || 'modern_horizontal',
    primaryColor: assignedTemplate?.primaryColor || '#0284c7',
    toggles: assignedTemplate?.toggles,
    socials: assignedTemplate?.socials,
    bannerUrl: assignedTemplate?.bannerUrl,
    bannerLink: assignedTemplate?.bannerLink || 'https://cerebrolabs.io',
    bannerAlt: assignedTemplate?.bannerAlt,
    promoTitle: assignedTemplate?.promoTitle,
    promoText: assignedTemplate?.promoText,
    promoCtaText: assignedTemplate?.promoCtaText,
    disclaimer: assignedTemplate?.disclaimer,
  };

  const signatureHtml = generateClientSignatureHtml(employeeData);

  const handleCopyCode = async () => {
    try {
      await copySignatureToClipboard(signatureHtml);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Błąd kopiowania do schowka:', err);
    }
  };

  const handleDownloadHtml = () => {
    const filename = `stopka-${(employeeData.fullName || 'pracownik').toLowerCase().replace(/[^a-z0-9]/g, '-')}.html`;
    downloadSignatureHtmlFile(signatureHtml, filename);
  };

  return (
    <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 16, width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
      {/* Header & Mode Tabs */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Sparkles size={18} color="#38bdf8" />
          <h2 style={{ fontSize: 16, fontWeight: 700, color: '#0B192C' }}>
            Podgląd Stopki (520px)
          </h2>
        </div>

        {/* Tab switchers */}
        <div style={{ display: 'flex', background: '#f1f5f9', borderRadius: 8, padding: 3, border: '1px solid #e2e8f0' }}>
          <button
            onClick={() => setActiveTab('visual')}
            className="btn"
            style={{
              padding: '4px 10px',
              fontSize: 12,
              background: activeTab === 'visual' ? '#0066FF' : 'transparent',
              color: activeTab === 'visual' ? '#ffffff' : '#475569',
              borderRadius: 6,
              fontWeight: activeTab === 'visual' ? 600 : 500,
            }}
          >
            <Eye size={13} />
            <span>Wizualny</span>
          </button>
          <button
            onClick={() => setActiveTab('code')}
            className="btn"
            style={{
              padding: '4px 10px',
              fontSize: 12,
              background: activeTab === 'code' ? '#0066FF' : 'transparent',
              color: activeTab === 'code' ? '#ffffff' : '#475569',
              borderRadius: 6,
              fontWeight: activeTab === 'code' ? 600 : 500,
            }}
          >
            <Code size={13} />
            <span>Kod HTML</span>
          </button>
        </div>
      </div>

      {/* Selected Employee Context */}
      <div
        style={{
          background: '#f8fafc',
          border: '1px solid #e2e8f0',
          borderRadius: 8,
          padding: '10px 14px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 10,
        }}
      >
        <div>
          <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <span>Aktywne konto</span>
            <span style={{ color: '#cbd5e1' }}>&bull;</span>
            <span style={{ color: '#0066FF', fontWeight: 600 }}>Szablon: {assignedTemplate?.name || 'Domyślny'}</span>
          </div>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#0B192C' }}>
            {employeeData.fullName} <span style={{ color: '#64748b', fontWeight: 400, fontSize: 12 }}>({employeeData.email})</span>
          </div>
        </div>

        {activeUser && (
          <button
            onClick={() => onInjectSingle(activeUser, signatureHtml)}
            disabled={isInjecting}
            className="btn btn-secondary"
            style={{ padding: '6px 12px', fontSize: 12 }}
            title="Wstrzyknij stopkę widoczną w podglądzie bezpośrednio do konta Gmail tego pracownika"
          >
            <Send size={13} />
            <span>{isInjecting ? 'Wstrzykiwanie...' : 'Wstrzyknij'}</span>
          </button>
        )}
      </div>

      {/* Preview Viewport */}
      {activeTab === 'visual' ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, width: '100%', maxWidth: '100%', overflowX: 'hidden' }}>
          {/* Email Client Simulated Container */}
          <div
            style={{
              background: '#f8fafc',
              borderRadius: 12,
              padding: '16px 12px',
              border: '1px solid #cbd5e1',
              boxShadow: 'inset 0 2px 8px rgba(0,0,0,0.08)',
              overflowX: 'auto',
              maxWidth: '100%',
              boxSizing: 'border-box',
              WebkitOverflowScrolling: 'touch',
            }}
          >
            {/* Simulated Email Header */}
            <div
              style={{
                borderBottom: '1px solid #e2e8f0',
                paddingBottom: 10,
                marginBottom: 14,
                fontSize: 12,
                color: '#475569',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span><strong>Od:</strong> {employeeData.fullName} &lt;{employeeData.email}&gt;</span>
                <span style={{ color: '#64748b' }}>Dziś, 10:45</span>
              </div>
              <div style={{ color: '#1e293b', fontWeight: 600, marginTop: 4 }}>
                Temat: Aktualizacja projektu CerebroLabs &bull; Wycena i specyfikacja techniczna
              </div>
            </div>

            {/* Email Body mock text */}
            <div style={{ fontSize: 13, color: '#334155', lineHeight: 1.5, marginBottom: 18 }}>
              Dzień dobry,<br /><br />
              W załączeniu przesyłam zaktualizowaną dokumentację architektoniczną oraz harmonogram wdrożenia. W razie pytań pozostaję do dyspozycji.
              <br /><br />
              Pozdrawiam serdecznie,
            </div>

            {/* HTML Signature Render Container with horizontal overflow protection */}
            <div
              style={{
                width: '100%',
                maxWidth: '100%',
                minWidth: 0,
                overflowX: 'auto',
              }}
              dangerouslySetInnerHTML={{ __html: signatureHtml }}
            />
          </div>

          {/* Quick parameter tuning */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 4 }}>
            <div>
              <label style={{ fontSize: 11, color: '#64748b', display: 'block', marginBottom: 4 }}>
                Testuj Stanowisko:
              </label>
              <input
                type="text"
                placeholder={activeUser?.jobTitle || 'np. Solutions Architect'}
                value={customTitle}
                onChange={(e) => setCustomTitle(e.target.value)}
                className="input-control"
                style={{ fontSize: 12, padding: '6px 10px' }}
              />
            </div>
            <div>
              <label style={{ fontSize: 11, color: '#64748b', display: 'block', marginBottom: 4 }}>
                Testuj Telefon:
              </label>
              <input
                type="text"
                placeholder={activeUser?.phone || 'np. +48 600 000 000'}
                value={customPhone}
                onChange={(e) => setCustomPhone(e.target.value)}
                className="input-control"
                style={{ fontSize: 12, padding: '6px 10px' }}
              />
            </div>
          </div>

          {/* Quick Copy & Download Actions in Visual Mode */}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4, flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={handleCopyCode}
              className="btn btn-outline"
              style={{ padding: '6px 12px', fontSize: 12 }}
            >
              {copied ? <Check size={14} color="#10b981" /> : <Copy size={14} />}
              <span>{copied ? 'Skopiowano!' : 'Kopiuj HTML'}</span>
            </button>
            <button
              type="button"
              onClick={handleDownloadHtml}
              className="btn btn-outline"
              style={{ padding: '6px 12px', fontSize: 12 }}
              title="Pobierz plik .html z kodowaniem UTF-8"
            >
              <Download size={14} />
              <span>Pobierz HTML</span>
            </button>
          </div>
        </div>
      ) : (
        /* Raw HTML Code View */
        <div style={{ position: 'relative' }}>
          <div
            style={{
              position: 'absolute',
              top: 10,
              right: 10,
              display: 'flex',
              gap: 8,
              zIndex: 10,
            }}
          >
            <button
              onClick={handleCopyCode}
              className="btn btn-primary"
              style={{
                padding: '6px 12px',
                fontSize: 12,
              }}
            >
              {copied ? <Check size={14} color="#fff" /> : <Copy size={14} />}
              <span>{copied ? 'Skopiowano!' : 'Kopiuj kod HTML'}</span>
            </button>

            <button
              onClick={handleDownloadHtml}
              className="btn btn-outline"
              style={{
                padding: '6px 12px',
                fontSize: 12,
                background: '#ffffff',
                color: '#0B192C',
                borderColor: '#cbd5e1',
              }}
              title="Pobierz plik .html z poprawnym kodowaniem UTF-8"
            >
              <Download size={14} />
              <span>Pobierz HTML</span>
            </button>
          </div>

          <pre
            style={{
              background: '#080c16',
              border: '1px solid #1e293b',
              borderRadius: 8,
              padding: '14px',
              paddingTop: '44px',
              maxHeight: '440px',
              overflowY: 'auto',
              fontSize: 11,
              fontFamily: 'monospace',
              color: '#38bdf8',
              lineHeight: 1.4,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-all',
            }}
          >
            {signatureHtml}
          </pre>
        </div>
      )}
    </div>
  );
};
